import { useState, useMemo, useRef, useEffect } from "react";
import { NAVIGATION_LINK_CLICKED, FILTER_TYPING_STARTED, READ_MORE_CLICKED } from "constants/bigbrain-event-types";
import SecondaryButton from "segments/desktop/core-components/button/secondary-button/secondary-button";
import { ID_TO_OPTIONS_NAMES_AND_COLORS } from "components/pages/whats-new/consts";
import { componentStyles } from "./whats-new-items-list.scss";
import WhatsNewItemTag from "./tag/tag";
import WhatsNewPageFilters from "./filters/filters";
import { filterItems, PRODUCT_TAG_TYPE, TEAM_TAG_TYPE, RELEASE_STATUS_TAG_TYPE, RELEASE_TYPE_TAG_TYPE } from "../utils";
import AssetComponent from "/components/core/asset/asset-component";
import { trackEvent } from "/services/bigbrain-service";
import { filtersSchema } from "../consts";
import { getCleanedFilterSchema } from "../utils";
import { generateOnKeyDownHandler } from "/client/services/accessibility/accessibility-service";
import YouTubePopupButton from "segments/desktop/core-components/button/youtube-popup-button/youtube-popup-button";
import { BRAND_BLACK_TEXT_COLOR } from "styles/color-consts";
import classnames from "classnames";
import { getReleaseTypeIcon } from "components/pages/whats-new/utils";
import Title from "segments/desktop/core-components/title/title";
import { SM } from "constants/sizes";
import { getUrlParamValueFromCurrentLocation } from "utils/url";

const initialFilterValues = filtersSchema.reduce((acc, { id }) => {
  acc[id] = [];
  return acc;
}, {});

const WhatsNewPageItemsList = (props) => {
  const {
    itemsArray,
    textSearch: textSearchProp,
    filterValues: filterValuesProp,
    isMobile,
    monthlyVideosSet,
    productFilterValue
  } = props;

  const [page, setPage] = useState(1);
  const [filterValues, setFilterValues] = useState(filterValuesProp || initialFilterValues);
  const [textSearch, setTextSearch] = useState(textSearchProp || "");
  const MOBILE_ITEMS_PER_PAGE_COUNT = 8;
  const DESKTOP_ITEMS_PER_PAGE_COUNT = 400;
  const itemsPerPage = isMobile ? MOBILE_ITEMS_PER_PAGE_COUNT : DESKTOP_ITEMS_PER_PAGE_COUNT;
  const DEFAULT_IMAGE_URL =
    "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Whats%20new%20page/3.png";
  const EMPTY_STATE_IMAGE_URL =
    "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Whats%20new%20page/empty_state_image.svg";
  const WATCH_NOW_TEXT = "Watch now";
  const ALL_UPDATES_AND_RELEASES = "All Updates & Releases";

  const teamFilter = getUrlParamValueFromCurrentLocation("team");

  useEffect(() => {
    let newFilterValues = filterValuesProp || initialFilterValues;

    if (teamFilter && Object.keys(ID_TO_OPTIONS_NAMES_AND_COLORS.team.data).includes(teamFilter)) {
      const tempFilterValues = { ...newFilterValues, team: [teamFilter] };
      const tempFilteredItems = filterItems(itemsArray, tempFilterValues, textSearch);
      if (tempFilteredItems.length > 0) {
        newFilterValues = tempFilterValues;
      }
    }

    if (productFilterValue && Object.keys(ID_TO_OPTIONS_NAMES_AND_COLORS.product.data).includes(productFilterValue)) {
      const tempFilterValues = { ...newFilterValues, product: [productFilterValue] };
      const tempFilteredItems = filterItems(itemsArray, tempFilterValues, textSearch);
      if (tempFilteredItems.length > 0) {
        newFilterValues = tempFilterValues;
      }
    }

    setFilterValues(newFilterValues);
  }, [filterValuesProp, teamFilter, itemsArray, textSearch, productFilterValue]);

  const filteredItems = useMemo(() => {
    itemsArray.sort((a, b) => {
      const dateA = new Date(a.fullDate);
      const dateB = new Date(b.fullDate);
      return dateB - dateA;
    });
    return filterItems(itemsArray, filterValues, textSearch);
  }, [itemsArray, filterValues, textSearch]);

  const totalPageCount = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const itemsExist = paginatedItems.length > 0;

  const squareListRef = useRef();

  const cleanedFiltersSchema = useMemo(
    () => getCleanedFilterSchema(filtersSchema, itemsArray),
    [filtersSchema, itemsArray]
  );

  const scrollToTop = () => {
    const offsetTop = squareListRef.current.offsetTop;
    const headerOffset = isMobile ? 200 : 100;
    window.scrollTo({ top: offsetTop - headerOffset, behavior: "smooth" });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    scrollToTop();
    trackEvent(NAVIGATION_LINK_CLICKED);
  };

  const handleFiltersChange = (newFilterValues) => {
    setFilterValues(newFilterValues);
    setPage(1);
    scrollToTop();
  };

  const handleTextSearchChange = (event) => {
    if (textSearch?.length === 0) {
      trackEvent(FILTER_TYPING_STARTED);
    }
    setTextSearch(event.target.value);
    setPage(1);
  };

  const renderPagination = () => {
    return (
      <div className="pagination-wrapper">
        <div className="line-dot-container">
          <div className="vertical-line" />
        </div>
        <div className="pagination">
          {page > 1 && (
            <span
              className="pagination-button"
              onClick={() => handlePageChange(page - 1)}
              tabIndex={0}
              onKeyDown={generateOnKeyDownHandler(() => {
                handlePageChange(page + -1);
              })}
              role="button"
              aria-label="Previous page"
            >
              {"< Back"}
            </span>
          )}
          <span>
            {page} / {totalPageCount}
          </span>
          {page < totalPageCount && (
            <span
              className="pagination-button"
              onClick={() => handlePageChange(page + 1)}
              tabIndex={0}
              onKeyDown={generateOnKeyDownHandler(() => {
                handlePageChange(page + 1);
              })}
              role="button"
              aria-label="Next page"
            >
              {"Next >"}
            </span>
          )}
        </div>
      </div>
    );
  };

  const readMoreClicked = (item) => {
    trackEvent(READ_MORE_CLICKED, { kind: item?.title });
  };

  const renderEmptyMessage = () => {
    return (
      <div className="empty-state">
        <div className="empty-state">
          <div className="empty-message-title">No results found</div>
          <div className="empty-message-subtitle">Please edit your search and try again.</div>
        </div>
        <AssetComponent
          image={{
            src: EMPTY_STATE_IMAGE_URL
          }}
        />
      </div>
    );
  };

  const renderTags = (item) => {
    return (
      <div className="tags-wrapper">
        <div className="left-tags-wrapper">
          {item.product != null && <WhatsNewItemTag tagId={item.product} tagType={PRODUCT_TAG_TYPE} />}
          {isMobile && item.releaseType != null && (
            <WhatsNewItemTag tagId={item.releaseType} tagType={RELEASE_TYPE_TAG_TYPE} />
          )}
          {item.team != null && <WhatsNewItemTag tagId={item.team} tagType={TEAM_TAG_TYPE} />}
        </div>
      </div>
    );
  };

  const renderItem = (item, index) => {
    const currentItemFormattedDate = item.monthDate;
    const lastItemFormattedDate = paginatedItems[index - 1]?.monthDate;
    const showDateTitle = isMobile || currentItemFormattedDate !== lastItemFormattedDate;
    const monthlyVideoId = item.monthYearDate ? monthlyVideosSet?.get(item.monthYearDate) : null;
    const releaseTypeName = ID_TO_OPTIONS_NAMES_AND_COLORS[RELEASE_TYPE_TAG_TYPE]?.data[item.releaseType]?.name;
    const releaseTypeColor = ID_TO_OPTIONS_NAMES_AND_COLORS[RELEASE_TYPE_TAG_TYPE]?.data[item.releaseType]?.color;

    const isFirstItemOnPage = index === 0;
    const isLastItem = index === paginatedItems.length - 1;

    const BOTTOM_CARD_MARGIN = "24px";

    return (
      <>
        {showDateTitle && !isMobile && (
          <div className="desktop-date-title">
            <div className="line-dot-container">
              <div className={classnames("vertical-line", { "page-first-item": isFirstItemOnPage })} />
              <div className="date-dot">
                <div className="inner-dot" />
                <div className="outer-ring" />
              </div>
            </div>
            <div className="date-content">
              <b>{item.monthDate}</b>
              {monthlyVideoId && (
                <div className="popup-button-wrapper">
                  &nbsp;|&nbsp;
                  <YouTubePopupButton
                    buttonText={WATCH_NOW_TEXT}
                    isWithIcon={true}
                    isWithIconInsteadOfArrow={true}
                    withoutUnderline={true}
                    iconColor={BRAND_BLACK_TEXT_COLOR}
                    videoId={monthlyVideoId}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        <div className="square-wrapper" key={`${item?.title}`}>
          <div className="line-dot-container">
            <div className="vertical-line" />
            <div className="dot" />
          </div>
          {showDateTitle && isMobile && <div className="mobile-date-title">{item.fullDate}</div>}
          <div className="square" style={{ marginBottom: isLastItem ? BOTTOM_CARD_MARGIN : 0 }}>
            <div className="content-wrapper">
              {isMobile && renderTags(item)}
              {!isMobile && (
                <div className="card-top-part-wrapper">
                  <div className="release-type-icon">{getReleaseTypeIcon(item.releaseType)}</div>
                  <div className="release-type-name" style={{ color: releaseTypeColor }}>
                    {releaseTypeName}
                  </div>
                  <span className="separator-dot" style={{ color: releaseTypeColor }}>
                    •
                  </span>
                  <div className="full-date-title">{item.partialDate}</div>
                </div>
              )}

              <div className="name">{item.title}</div>
              <div className="description">{item.shortText}</div>
              {item.articleLink && (
                <div className="read-more" onClick={() => readMoreClicked(item)}>
                  <SecondaryButton
                    buttonText="Read more"
                    url={item.articleLink}
                    withoutUnderline={true}
                    isOpenLinkInNewTab={true}
                  />
                </div>
              )}

              {!isMobile && renderTags(item)}

              <div className="image-wrapper mobile-image-wrapper">
                <AssetComponent
                  image={{
                    src: item?.cloudinaryUrl || DEFAULT_IMAGE_URL
                  }}
                />
              </div>
            </div>
            <div className="image-wrapper desktop-image-wrapper">
              <AssetComponent
                image={{
                  src: item?.desktopCloudinaryUrl || DEFAULT_IMAGE_URL
                }}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="square-list-container" ref={squareListRef}>
      {!isMobile && (
        <WhatsNewPageFilters
          filtersSchema={cleanedFiltersSchema}
          filterValues={filterValues}
          onChange={handleFiltersChange}
          setFilterValues={setFilterValues}
          handleTextSearchChange={handleTextSearchChange}
          textSearch={textSearch}
          itemsArray={itemsArray}
        />
      )}

      <div className={classnames("square-list", { "empty-list": !itemsExist })}>
        {!isMobile && itemsExist && (
          <div className="square-list-title">
            <Title title={ALL_UPDATES_AND_RELEASES} titleSize={SM} />
          </div>
        )}
        {itemsExist ? paginatedItems.map((item, index) => renderItem(item, index)) : renderEmptyMessage()}

        {paginatedItems.length === 1 && (
          <div className={classnames("square-wrapper", "placeholder")}>
            <div className="line-dot-container">
              <div className="vertical-line" />
            </div>
          </div>
        )}

        {totalPageCount > 1 && renderPagination()}

        <style jsx>{componentStyles}</style>
      </div>
    </div>
  );
};

export default WhatsNewPageItemsList;
